import { useState } from "react";

import { MoreVertical, Play } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import type { GroupColor } from "@/pages/tina/groups/lib/group-color";
import type { GroupBarSegment } from "@/pages/tina/groups/lib/weekly-layout";
import type { TinaSiteGroup } from "@/types/tina";

export interface GroupBarProps {
  group: TinaSiteGroup;
  /** Per-group colour (stripe + fills) from buildGroupColors — stable per group. */
  color?: GroupColor;
  /** grid-column placement within the row grid, from buildWeeklyLayout. */
  gridColumn: string;
  /** Day segments from first → last run day (run/off), from buildWeeklyLayout. */
  segments: GroupBarSegment[];
  /** ":MM" shown before the name when the group runs off the hour; null on the hour. */
  minuteLabel?: string | null;
  onEdit: (id: string) => void;
  onRunNow: (id: string) => void;
  onDelete: (id: string) => void;
  isBusy?: boolean;
}

/**
 * A single group as a spanning bar in the weekly calendar. Covers the days it
 * runs edge-to-edge (segments align with the column hairlines because the bar
 * carries no horizontal margin), overlays time · name · run-now · site count ·
 * kebab, and behaves like the old card: click to edit, run-now / kebab stop
 * propagation, delete asks for confirmation first.
 */
export function GroupBar({
  group,
  color,
  gridColumn,
  segments,
  minuteLabel = null,
  onEdit,
  onRunNow,
  onDelete,
  isBusy = false,
}: GroupBarProps) {
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  return (
    <div
      className={cn(
        "group relative z-[1] grid h-[34px] cursor-pointer items-center overflow-hidden rounded-md text-[13px] transition-[filter] duration-150 hover:brightness-125",
        !group.enabled && "opacity-50",
        isBusy && "opacity-60",
      )}
      style={{
        gridColumn,
        gridRow: 1,
        gridTemplateColumns: `repeat(${Math.max(segments.length, 1)}, 1fr)`,
      }}
      onClick={() => onEdit(group.id)}
    >
      {segments.map((seg, i) => (
        <span
          key={i}
          style={{
            gridRow: 1,
            gridColumn: i + 1,
            height: 34,
            background: seg.active ? color?.runFill : color?.offFill,
            borderLeft: i === 0 ? `3px solid ${color?.stripe}` : undefined,
          }}
        />
      ))}

      <span
        className="z-[1] flex min-w-0 items-center gap-2 px-3"
        style={{ gridRow: 1, gridColumn: "1 / -1" }}
      >
        {minuteLabel ? (
          <span
            className="flex-none text-xs font-semibold tabular-nums"
            style={{ color: "oklch(0.985 0 0 / 0.75)" }}
          >
            {minuteLabel}
          </span>
        ) : null}
        <span
          className="min-w-0 flex-1 truncate font-medium"
          title={group.name}
        >
          {group.name}
        </span>
        <button
          type="button"
          className="flex h-6 flex-none items-center gap-1 rounded-md border border-input bg-secondary/80 px-2 text-[11px] font-medium opacity-0 transition-opacity duration-150 ease-out hover:bg-accent group-hover:opacity-100 disabled:pointer-events-none focus-visible:opacity-100"
          title="Run now"
          disabled={isBusy}
          onClick={(e) => {
            e.stopPropagation();
            onRunNow(group.id);
          }}
        >
          <Play className="h-2.5 w-2.5" />
          Run now
        </button>
        <span
          className="flex-none text-[11.5px] tabular-nums text-muted-foreground"
          aria-label={`${group.member_count} sites`}
          title={`${group.member_count} sites`}
        >
          {group.member_count}
        </span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="flex flex-none items-center justify-center text-muted-foreground disabled:opacity-50"
              aria-label="Group actions"
              disabled={isBusy}
            >
              <MoreVertical className="h-[13px] w-[13px]" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
            <DropdownMenuItem
              onClick={() => onEdit(group.id)}
              disabled={isBusy}
            >
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-destructive"
              disabled={isBusy}
              onClick={() => setConfirmDeleteOpen(true)}
            >
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </span>

      <Dialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <DialogContent onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Delete group?</DialogTitle>
            <DialogDescription>
              This will permanently delete “{group.name}”. This action cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmDeleteOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setConfirmDeleteOpen(false);
                onDelete(group.id);
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
